import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  Flex,
  IconButton,
  Select,
  Separator,
  Switch,
  Text,
  TextArea,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import { ArrowLeftIcon, ChatBubbleIcon, PlusIcon, TrashIcon, UploadIcon } from "@radix-ui/react-icons";
import { ToastAlert } from "components";
import { useProfileContext } from "context";
import { useGetWhatsappAccountPhoneNumbers, useMarketingWhatsappTemplates } from "hooks";
import { useToast } from "../../context/ToastContext";
import MarketingWhatsappPreview from "./MarketingWhatsappPreview";
import {
  DEFAULT_WHATSAPP_TEMPLATE,
  getWhatsappButtonValueLabel,
  getWhatsappHeaderTypeLabel,
  getWhatsappTemplateStatusLabel,
  WHATSAPP_BUTTON_TYPES,
  WHATSAPP_HEADER_TYPES,
  WHATSAPP_TEMPLATE_LANGUAGES,
} from "./marketingWhatsappStore";

const HEADER_ATTACHMENT_ACCEPT = {
  image: "image/*",
  video: "video/*",
  document: "application/pdf,.pdf",
};

const HEADER_ATTACHMENT_MAX_BYTES = 1048576;
const LOCKED_EDITOR_TOOLTIP = "You cannot edit this template while it is in review.";
const TEST_SEND_ALLOWED_STATUSES = new Set(["approved"]);
function FieldLabel({ children, detail }) {
  return (
    <Flex justify="between" align="center" gap="3">
      <Text size="2" weight="medium">
        {children}
      </Text>
      {detail ? (
        <Text size="1" color="gray">
          {detail}
        </Text>
      ) : null}
    </Flex>
  );
}

function LockedEditorTooltip({ locked, children }) {
  if (!locked) return children;
  return <Tooltip content={LOCKED_EDITOR_TOOLTIP}>{children}</Tooltip>;
}

function buildFallbackTemplate(templateId) {
  return {
    id: templateId,
    ...DEFAULT_WHATSAPP_TEMPLATE,
  };
}

function normalizeWhatsappTemplate(template) {
  const legacyMediaHeader = template.mediaEnabled || (template.headerType && template.headerType !== "text" && template.headerType !== "none");
  const headerType = legacyMediaHeader ? (template.mediaType || template.headerType || "image") : template.headerEnabled ? "text" : "none";
  const headerMediaLabel = template.headerMediaLabel || template.mediaLabel || "";

  return {
    ...DEFAULT_WHATSAPP_TEMPLATE,
    ...template,
    language: template.language || "",
    headerType,
    headerMediaLabel,
    headerAttachment: template.headerAttachment || null,
    headerEnabled: headerType !== "none",
    headerText: legacyMediaHeader ? "" : template.headerText || "",
    mediaEnabled: !["none", "text"].includes(headerType),
    mediaType: !["none", "text"].includes(headerType) ? headerType : "",
    mediaLabel: headerMediaLabel,
  };
}

function buildWhatsappTemplatePayload(formData, savedSnapshot) {
  const payload = {
    name: formData.name,
    language: formData.language,
    header_enabled: formData.headerEnabled,
    header_type: formData.headerEnabled ? formData.headerType : "none",
    header_text: formData.headerEnabled && formData.headerType === "text" ? formData.headerText : "",
    body: formData.body,
    footer_enabled: formData.footerEnabled,
    footer_text: formData.footerEnabled ? formData.footerText : "",
    buttons: formData.buttons,
  };
  const headerFile = formData.headerAttachment?.file;
  const shouldRemoveHeaderAttachment = Boolean(savedSnapshot.headerAttachment?.url) && !formData.headerAttachment;

  if (!headerFile && !shouldRemoveHeaderAttachment) {
    return payload;
  }

  const multipartPayload = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    multipartPayload.append(key, key === "buttons" ? JSON.stringify(value) : value);
  });

  if (headerFile) {
    multipartPayload.append("header_attachment", headerFile);
  }

  if (shouldRemoveHeaderAttachment) {
    multipartPayload.append("remove_header_attachment", "true");
  }

  return multipartPayload;
}

export default function MarketingWhatsappTemplateEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId } = useParams();
  const { userProfile } = useProfileContext();
  const { setToastMessage } = useToast();
  const headerAttachmentInputRef = useRef(null);
  const [formData, setFormData] = useState(normalizeWhatsappTemplate(location.state?.template || buildFallbackTemplate(templateId)));
  const [savedSnapshot, setSavedSnapshot] = useState(formData);
  const [headerAttachmentError, setHeaderAttachmentError] = useState("");
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testPhoneNumberId, setTestPhoneNumberId] = useState("");
  const [testSenderError, setTestSenderError] = useState("");
  const [testPhoneError, setTestPhoneError] = useState("");
  const {
    whatsappTemplates,
    sendTestTemplateAsync,
    submitTemplateForReviewAsync,
    isSendingTest,
    isLoading,
    isSubmittingForReview,
  } = useMarketingWhatsappTemplates(userProfile?.organisation_id);
  const whatsappConfigId = formData.whatsappConfig;
  const { data: phoneNumbersData, isLoading: isLoadingPhoneNumbers } = useGetWhatsappAccountPhoneNumbers(
    userProfile?.organisation_id,
    whatsappConfigId,
    testDialogOpen,
  );
  const whatsappPhoneNumbers = useMemo(() => phoneNumbersData?.data?.phone_numbers || [], [phoneNumbersData]);
  const storedTemplate = useMemo(() => {
    return whatsappTemplates.find((template) => String(template.id) === String(templateId)) || null;
  }, [templateId, whatsappTemplates]);

  useEffect(() => {
    const nextTemplate = normalizeWhatsappTemplate(storedTemplate || location.state?.template || buildFallbackTemplate(templateId));
    setFormData(nextTemplate);
    setSavedSnapshot(nextTemplate);
  }, [location.state?.template, storedTemplate, templateId]);

  useEffect(() => {
    if (!testDialogOpen) return;

    const senderStillAvailable = whatsappPhoneNumbers.some((phoneNumber) => String(phoneNumber.id) === String(testPhoneNumberId));
    if (senderStillAvailable) return;

    setTestPhoneNumberId(whatsappPhoneNumbers.length === 1 ? String(whatsappPhoneNumbers[0].id) : "");
  }, [testDialogOpen, testPhoneNumberId, whatsappPhoneNumbers]);

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(savedSnapshot);
  }, [formData, savedSnapshot]);
  const isLocked = Boolean(formData.isLockedForEditing);
  const reviewStatusLabel = formData.reviewStatus && formData.reviewStatus !== "draft"
    ? getWhatsappTemplateStatusLabel(formData.reviewStatus, formData.qualityScore)
    : "";
  const canSendTest = TEST_SEND_ALLOWED_STATUSES.has(formData.reviewStatus);
  const requiresHeaderAttachment = Boolean(formData.headerEnabled && HEADER_ATTACHMENT_ACCEPT[formData.headerType]);
  const canSubmitForReview = Boolean(formData.name.trim()) && Boolean(formData.language) && Boolean(formData.body.trim()) && (!requiresHeaderAttachment || Boolean(formData.headerAttachment));
  const hasMetaTemplate = Boolean(formData.metaTemplateId || formData.meta_template_id);
  const submitActionLabel = hasMetaTemplate ? "Submit update" : "Submit for review";
  const canSubmitTemplate = canSubmitForReview && (!hasMetaTemplate || hasUnsavedChanges);

  const updateForm = (updates) => {
    if (isLocked) return;
    setFormData((current) => ({
      ...current,
      ...updates,
    }));
  };

  const updateButton = (buttonId, updates) => {
    updateForm({
      buttons: formData.buttons.map((button) => (button.id === buttonId ? { ...button, ...updates } : button)),
    });
  };

  const addButton = () => {
    if (isLocked) return;
    if (formData.buttons.length >= 3) return;
    updateForm({
      buttons: [
        ...formData.buttons,
        {
          id: `btn-${Date.now()}`,
          type: "website",
          label: "Visit website",
          value: "https://example.com",
        },
      ],
    });
  };

  const removeButton = (buttonId) => {
    if (isLocked) return;
    updateForm({
      buttons: formData.buttons.filter((button) => button.id !== buttonId),
    });
  };

  const handleHeaderEnabledChange = (checked) => {
    updateForm({
      headerEnabled: checked,
      headerType: checked ? (formData.headerType && formData.headerType !== "none" ? formData.headerType : "text") : "none",
      headerText: checked ? formData.headerText : "",
      headerMediaLabel: checked && !["none", "text"].includes(formData.headerType) ? formData.headerMediaLabel : "",
      headerAttachment: checked && !["none", "text", "location"].includes(formData.headerType) ? formData.headerAttachment : null,
      mediaEnabled: checked && !["none", "text"].includes(formData.headerType),
      mediaType: checked && !["none", "text"].includes(formData.headerType) ? formData.headerType : "",
      mediaLabel: checked && !["none", "text"].includes(formData.headerType) ? formData.headerMediaLabel : "",
    });
  };

  const handleHeaderTypeChange = (value) => {
    setHeaderAttachmentError("");
    updateForm({
      headerType: value,
      headerEnabled: true,
      headerText: value === "text" ? formData.headerText : "",
      headerMediaLabel: "",
      headerAttachment: null,
      mediaEnabled: value !== "text",
      mediaType: value !== "text" ? value : "",
      mediaLabel: "",
    });
  };

  const handleHeaderAttachmentChange = (event) => {
    if (isLocked) return;
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > HEADER_ATTACHMENT_MAX_BYTES) {
      setHeaderAttachmentError("Maximum file size is 1 MB.");
      event.target.value = "";
      return;
    }

    if (formData.headerType === "document" && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setHeaderAttachmentError("Document headers only support PDF files.");
      event.target.value = "";
      return;
    }

    setHeaderAttachmentError("");
    const attachmentUrl = URL.createObjectURL(file);
    const attachment = {
      name: file.name,
      type: file.type,
      size: file.size,
      url: attachmentUrl,
      file,
    };

    updateForm({
      headerAttachment: attachment,
      headerMediaLabel: file.name,
      mediaLabel: file.name,
    });
  };

  const handleHeaderAttachmentClear = () => {
    if (headerAttachmentInputRef.current) {
      headerAttachmentInputRef.current.value = "";
    }
    setHeaderAttachmentError("");
    updateForm({
      headerAttachment: null,
      headerMediaLabel: "",
      mediaLabel: "",
    });
  };

  const handleSubmitForReview = async () => {
    if (isLocked) return;
    try {
      const payload = buildWhatsappTemplatePayload(formData, savedSnapshot);
      const submittedTemplate = await submitTemplateForReviewAsync({ templateId, data: payload });
      setFormData(submittedTemplate);
      setSavedSnapshot(submittedTemplate);
      setToastMessage(hasMetaTemplate ? "WhatsApp template update submitted to Meta" : "WhatsApp template submitted to Meta for review");
    } catch (error) {
      const responseData = error?.response?.data;
      if (responseData?.template_id) {
        const failedTemplate = normalizeWhatsappTemplate(responseData);
        setFormData(failedTemplate);
        setSavedSnapshot(failedTemplate);
      }
      const message = responseData?.error || responseData?.meta_publish_error || "Unable to submit WhatsApp template for review right now";
      setToastMessage(message);
    }
  };

  const handleTestDialogOpenChange = (open) => {
    setTestDialogOpen(open);
    if (!open) {
      setTestPhone("");
      setTestPhoneNumberId("");
      setTestSenderError("");
      setTestPhoneError("");
    }
  };

  const handleSendTest = async () => {
    const cleanedPhone = testPhone.trim();
    const whatsappPhoneNumber = cleanedPhone.replace(/\D/g, "");

    if (!canSendTest) {
      setTestPhoneError("Meta must approve the template before you can send a test message");
      return;
    }

    if (!testPhoneNumberId) {
      setTestSenderError("Select a sender phone number");
      return;
    }

    if (!cleanedPhone) {
      setTestPhoneError("Enter a phone number");
      return;
    }

    if (whatsappPhoneNumber.length < 7) {
      setTestPhoneError("Enter a valid phone number");
      return;
    }

    try {
      const response = await sendTestTemplateAsync({
        templateId,
        data: {
          phone_number: whatsappPhoneNumber,
          phone_number_id: testPhoneNumberId,
        },
      });
      setTestDialogOpen(false);
      setTestPhone("");
      setTestPhoneNumberId("");
      setTestSenderError("");
      setTestPhoneError("");
      setToastMessage(response.data?.message || `Test WhatsApp message sent to ${whatsappPhoneNumber}`);
    } catch (error) {
      setTestPhoneError(error?.response?.data?.error || "Unable to send the WhatsApp test message right now");
    }
  };

  const handleDiscard = () => {
    setFormData(savedSnapshot);
  };

  return (
    <>
    <Flex direction="column" p="5" gap="5" style={{ width: "100%", height: "100%", flex: "1 0 0", overflow: "hidden" }}>
      <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
        <Flex align="center" gap="3" style={{ minWidth: 0 }}>
          <IconButton variant="soft" color="gray" onClick={() => navigate("/marketing/templates", { state: { selectedSegment: "whatsapp" } })}>
            <ArrowLeftIcon />
          </IconButton>
          <Box style={{ minWidth: 0 }}>
            <Text as="div" size="5" weight="bold" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {formData.name || "WhatsApp template"}
            </Text>
          </Box>
        </Flex>
        <Flex align="center" gap="2">
          {reviewStatusLabel ? (
            <Text size="2" color="gray" weight="medium">
              {reviewStatusLabel}
            </Text>
          ) : null}
          <Tooltip content={canSendTest ? "Send a test WhatsApp message" : "Meta must approve this template before you can send a test message"}>
            <span style={{ display: "inline-flex" }}>
              <Button
                variant="soft"
                color="gray"
                disabled={!canSendTest || isSendingTest}
                onClick={() => setTestDialogOpen(true)}
              >
                <ChatBubbleIcon width="16" height="16" />
                {isSendingTest ? "Sending..." : "Send test"}
              </Button>
            </span>
          </Tooltip>
        </Flex>
      </Flex>

      <Flex gap="5" style={{ flex: "1 0 0", overflow: "hidden" }}>
        <Flex style={{ alignItems: "flex-start", alignSelf: "stretch", overflow: "hidden" }} height="100%">
          <LockedEditorTooltip locked={isLocked}>
            <Flex
              width="420px"
              direction="column"
              style={{
                alignItems: "flex-start",
                alignSelf: "stretch",
                flex: "1 0 0",
                borderRadius: "var(--radius-4)",
                height: "100%",
                border: "1px solid var(--gray-a6)",
                backgroundColor: "var(--color-panel-solid)",
                overflow: "hidden",
              }}
            >
            <Flex direction="column" style={{ alignSelf: "stretch", overflow: "auto", flex: "1 0 0" }}>
              <Flex direction="column" gap="4" p="4">
                <Flex direction="column" gap="1">
                  <Text size="3" weight="bold">
                    Template setup
                  </Text>
                </Flex>
                <Flex direction="column" gap="1">
                  <FieldLabel>Template name</FieldLabel>
                  <TextField.Root disabled={isLocked} value={formData.name} onChange={(event) => updateForm({ name: event.target.value })} />
                </Flex>
                <Flex direction="column" gap="1">
                  <FieldLabel>Language</FieldLabel>
                  <Select.Root disabled={isLocked} value={formData.language || ""} onValueChange={(value) => updateForm({ language: value })}>
                    <Select.Trigger placeholder="Select language" style={{ width: "100%" }} />
                    <Select.Content>
                      {WHATSAPP_TEMPLATE_LANGUAGES.map((language) => (
                        <Select.Item key={language.value} value={language.value}>
                          {language.label}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </Flex>
              </Flex>

              <Separator size="4" style={{ minHeight: 1 }} />

              <Flex direction="column" gap="4" p="4">
                <Flex justify="between" align="start" gap="3">
                  <Flex direction="column" gap="1">
                    <Text size="3" weight="bold">
                      Header
                    </Text>
                  </Flex>
                  <Switch disabled={isLocked} checked={formData.headerEnabled} onCheckedChange={handleHeaderEnabledChange} />
                </Flex>

                {formData.headerEnabled ? (
                  <Flex direction="column" gap="1">
                    <FieldLabel>Header type</FieldLabel>
                    <Select.Root disabled={isLocked} value={formData.headerType === "none" ? "text" : formData.headerType || "text"} onValueChange={handleHeaderTypeChange}>
                      <Select.Trigger style={{ width: "100%" }} />
                      <Select.Content>
                        {WHATSAPP_HEADER_TYPES.map((headerType) => (
                          <Select.Item key={headerType.value} value={headerType.value}>
                            {headerType.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  </Flex>
                ) : null}

                {formData.headerEnabled && formData.headerType === "text" ? (
                  <Flex direction="column" gap="1">
                    <FieldLabel detail={`${formData.headerText.length}/60`}>Header text</FieldLabel>
                    <TextField.Root
                      maxLength={60}
                      disabled={isLocked}
                      placeholder="Weekend special"
                      value={formData.headerText}
                      onChange={(event) => updateForm({ headerText: event.target.value })}
                    />
                  </Flex>
                ) : null}

                {formData.headerEnabled && HEADER_ATTACHMENT_ACCEPT[formData.headerType] ? (
                  <Flex direction="column" gap="1">
                    <FieldLabel>{getWhatsappHeaderTypeLabel(formData.headerType)} attachment</FieldLabel>
                    <Flex gap="2" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                      <input
                        type="file"
                        disabled={isLocked}
                        accept={HEADER_ATTACHMENT_ACCEPT[formData.headerType]}
                        onChange={handleHeaderAttachmentChange}
                        ref={headerAttachmentInputRef}
                        style={{ display: "none" }}
                      />
                      <Button
                        type="button"
                        size="2"
                        variant="surface"
                        color="gray"
                        disabled={isLocked}
                        style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
                        onClick={() => headerAttachmentInputRef.current?.click()}
                      >
                        {formData.headerAttachment?.name ? (
                          isLocked ? (
                            <Text>
                              {formData.headerAttachment.name.length >= 18
                                ? `${formData.headerAttachment.name.slice(0, 18)}...`
                                : formData.headerAttachment.name}
                            </Text>
                          ) : (
                            <Tooltip content="Upload new to replace">
                              <Text>
                                {formData.headerAttachment.name.length >= 18
                                  ? `${formData.headerAttachment.name.slice(0, 18)}...`
                                  : formData.headerAttachment.name}
                              </Text>
                            </Tooltip>
                          )
                        ) : (
                          <>
                            <UploadIcon />
                            <Text>Upload</Text>
                          </>
                        )}
                      </Button>
                    {formData.headerAttachment?.name ? (
                      <Button type="button" size="2" variant="surface" color="gray" disabled={isLocked} onClick={handleHeaderAttachmentClear}>
                        Clear
                      </Button>
                    ) : null}
                    </Flex>
                    {headerAttachmentError ? (
                      <Text size="1" color="red">
                        {headerAttachmentError}
                      </Text>
                    ) : (
                      <Text size="1" color="gray">
                        Maximum file size: 1 MB.
                      </Text>
                    )}
                  </Flex>
                ) : null}

                {formData.headerEnabled && formData.headerType === "location" ? (
                  <Flex direction="column" gap="1">
                    <FieldLabel>Location header</FieldLabel>
                    <Flex
                      align="center"
                      p="3"
                      style={{
                        minHeight: 40,
                        border: "1px solid var(--gray-a6)",
                        borderRadius: "var(--radius-2)",
                        background: "var(--gray-a2)",
                      }}
                    >
                      <Text size="2" color="gray">
                        Location placeholder header
                      </Text>
                    </Flex>
                  </Flex>
                ) : null}
              </Flex>

              <Separator size="4" style={{ minHeight: 1 }} />

              <Flex direction="column" gap="4" p="4">
                <Flex direction="column" gap="1">
                  <Text size="3" weight="bold">
                    Body
                  </Text>
                </Flex>
                <Flex direction="column" gap="1">
                  <FieldLabel detail={`${formData.body.length}/1024`}>Message body</FieldLabel>
                  <TextArea disabled={isLocked} rows={8} maxLength={1024} value={formData.body} onChange={(event) => updateForm({ body: event.target.value })} />
                </Flex>
              </Flex>

              <Separator size="4" style={{ minHeight: 1 }} />

              <Flex direction="column" gap="4" p="4">
                <Flex justify="between" align="start" gap="3">
                  <Flex direction="column" gap="1">
                    <Text size="3" weight="bold">
                      Footer
                    </Text>
                    <Text size="1" color="gray">
                      Optional small print shown below the body.
                    </Text>
                  </Flex>
                  <Switch disabled={isLocked} checked={formData.footerEnabled} onCheckedChange={(checked) => updateForm({ footerEnabled: checked })} />
                </Flex>
                {formData.footerEnabled ? (
                  <Flex direction="column" gap="1">
                    <FieldLabel detail={`${formData.footerText.length}/60`}>Footer text</FieldLabel>
                    <TextField.Root
                      maxLength={60}
                      disabled={isLocked}
                      placeholder="Terms apply."
                      value={formData.footerText}
                      onChange={(event) => updateForm({ footerText: event.target.value })}
                    />
                  </Flex>
                ) : null}
              </Flex>

              <Separator size="4" style={{ minHeight: 1 }} />

              <Flex direction="column" gap="4" p="4">
                <Flex justify="between" align="start" gap="3">
                  <Flex direction="column" gap="1">
                    <Text size="3" weight="bold">
                      Buttons
                    </Text>
                    <Text size="1" color="gray">
                      Website, phone, and copy-code actions only.
                    </Text>
                  </Flex>
                  <Button size="1" variant="soft" onClick={addButton} disabled={isLocked || formData.buttons.length >= 3}>
                    <PlusIcon width="14" height="14" />
                    Add
                  </Button>
                </Flex>

                {formData.buttons.length === 0 ? (
                  <Flex align="center" justify="center" p="4" style={{ border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-3)" }}>
                    <Text size="2" color="gray">
                      No buttons added.
                    </Text>
                  </Flex>
                ) : (
                  <Flex direction="column" gap="3">
                    {formData.buttons.map((button, index) => (
                      <Box key={button.id} style={{ border: "1px solid var(--gray-a5)", borderRadius: "var(--radius-3)", padding: "var(--space-3)" }}>
                        <Flex direction="column" gap="3">
                          <Flex justify="between" align="center" gap="3">
                            <Text size="2" weight="medium">
                              Button {index + 1}
                            </Text>
                            <IconButton size="1" variant="ghost" color="red" disabled={isLocked} onClick={() => removeButton(button.id)}>
                              <TrashIcon />
                            </IconButton>
                          </Flex>

                          <Flex direction="column" gap="1">
                            <FieldLabel>Type</FieldLabel>
                            <Select.Root
                              value={button.type}
                              disabled={isLocked}
                              onValueChange={(value) =>
                                updateButton(button.id, {
                                  type: value,
                                  label: value === "copy_code" ? "Copy code" : value === "phone" ? "Call us" : "Visit website",
                                  value: value === "copy_code" ? "OFFER20" : value === "phone" ? "+44 " : "https://",
                                })
                              }
                            >
                              <Select.Trigger style={{ width: "100%" }} />
                              <Select.Content>
                                {WHATSAPP_BUTTON_TYPES.map((buttonType) => (
                                  <Select.Item key={buttonType.value} value={buttonType.value}>
                                    {buttonType.label}
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Root>
                          </Flex>
                          <Flex direction="column" gap="1">
                            <FieldLabel>Button text</FieldLabel>
                            <TextField.Root disabled={isLocked} value={button.label} onChange={(event) => updateButton(button.id, { label: event.target.value })} />
                          </Flex>
                          <Flex direction="column" gap="1">
                            <FieldLabel>{getWhatsappButtonValueLabel(button.type)}</FieldLabel>
                            <TextField.Root disabled={isLocked} value={button.value} onChange={(event) => updateButton(button.id, { value: event.target.value })} />
                          </Flex>
                        </Flex>
                      </Box>
                    ))}
                  </Flex>
                )}
              </Flex>
            </Flex>

            <Separator size="4" orientation="horizontal" style={{ minHeight: 1 }} />
            <Flex p="4" gap="2" style={{ alignSelf: "stretch" }}>
              <Button variant="soft" color="red" onClick={handleDiscard} disabled={isLocked || !hasUnsavedChanges} style={{ flex: 1 }}>
                Discard changes
              </Button>
              <Button onClick={handleSubmitForReview} disabled={isLocked || !canSubmitTemplate || isSubmittingForReview || isLoading} style={{ flex: 1 }}>
                {isSubmittingForReview ? "Submitting..." : submitActionLabel}
              </Button>
            </Flex>
            </Flex>
          </LockedEditorTooltip>
        </Flex>

        <Flex
          direction="column"
          style={{
            alignItems: "stretch",
            alignSelf: "stretch",
            flex: "1 1 0",
            minWidth: 0,
            borderRadius: "var(--radius-4)",
            overflow: "hidden",
            border: "1px solid var(--gray-a6)",
            backgroundColor: "var(--gray-a3)",
          }}
        >
          <Flex justify="between" align="center" p="4" style={{ background: "var(--color-panel-solid)", borderBottom: "1px solid var(--gray-a5)" }}>
            <Text size="3" weight="bold">
              Preview
            </Text>
            <Text size="2" color="green" weight="medium">WhatsApp</Text>
          </Flex>
          <Box style={{ padding: 12, overflow: "hidden", flex: "1 1 0", display: "flex", justifyContent: "center", alignItems: "center", minHeight: 0 }}>
            <MarketingWhatsappPreview template={formData} />
          </Box>
        </Flex>
      </Flex>
      <Dialog.Root open={testDialogOpen} onOpenChange={handleTestDialogOpenChange}>
        <Dialog.Content style={{ maxWidth: 460 }}>
          <Dialog.Title>Send WhatsApp test</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Send this approved template to a WhatsApp user phone number.
          </Dialog.Description>

          <Flex direction="column" gap="3" mt="4">
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">
                Sender phone number
              </Text>
              <Select.Root
                value={testPhoneNumberId}
                onValueChange={(value) => {
                  setTestPhoneNumberId(value);
                  setTestSenderError("");
                }}
                disabled={isLoadingPhoneNumbers || whatsappPhoneNumbers.length === 0}
              >
                <Select.Trigger
                  placeholder={isLoadingPhoneNumbers ? "Loading phone numbers..." : "Select sender phone number"}
                  style={{ width: "100%" }}
                />
                <Select.Content>
                  {whatsappPhoneNumbers.map((phoneNumber) => (
                    <Select.Item key={phoneNumber.id} value={String(phoneNumber.id)}>
                      {phoneNumber.display_phone_number || phoneNumber.id}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              {testSenderError ? (
                <Text size="1" color="red">
                  {testSenderError}
                </Text>
              ) : !isLoadingPhoneNumbers && whatsappPhoneNumbers.length === 0 ? (
                <Text size="1" color="red">
                  No sender phone numbers were found for this WhatsApp account.
                </Text>
              ) : null}
            </Flex>

            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">
                Recipient phone number
              </Text>
              <TextField.Root
                placeholder="+44 7700 900123"
                value={testPhone}
                onChange={(event) => {
                  setTestPhone(event.target.value);
                  setTestPhoneError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleSendTest();
                  }
                }}
              />
              {testPhoneError ? (
                <Text size="1" color="red">
                  {testPhoneError}
                </Text>
              ) : null}
            </Flex>
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" disabled={isSendingTest}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleSendTest} disabled={isSendingTest || !testPhoneNumberId}>
              {isSendingTest ? "Sending..." : "Send test"}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Flex>
    <ToastAlert />
    </>
  );
}
